if (!customElements.get('gr-video')) {
    customElements.define('gr-video', class extends HTMLElement {
        constructor() {
            super();
            this.video = null;
            this.preview = null;
            this.autoplay = false;
            this.observer = null;
        }

        connectedCallback() {
            this.video = this.lastElementChild;
            this.preview = this.querySelector('img')?.parentElement || null;
            this.autoplay = this.dataset.autoplay === 'true';
            if (!this.video) return;

            this.appendChild(this.video);

            this.observer = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting) {
                    this.video.play().catch(() => {
                        if (this.preview) this.preview.hidden = false;
                    });
                }
            });

            if (this.autoplay) this.observer.observe(this);

            this.addEventListener('click', () => {
                this.observer.disconnect();
                if (!this.video.hasAttribute('controls') || this.video.hidden) {
                    this.togglePlay();
                }
            }, { once: this.video.hasAttribute('controls') });

            this.video.addEventListener('play', () => {
                this.video.hidden = false;
                if (this.preview) this.preview.hidden = true;
            }, { once: true });
        }

        togglePlay() {
            if (this.video.paused) {
                this.video.play().catch(() => {
                    if (this.preview) this.preview.hidden = false;
                });
            } else {
                this.video.pause();
            }
        }

        disconnectedCallback() {
            this.observer?.disconnect();
        }
    });
}

if (!customElements.get('landing-features')) {
    customElements.define('landing-features', class extends HTMLElement {
        static SCROLL_KEYS = ['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Space', 'End', 'Home'];
        static NAV_KEYS = ['End', 'Home', 'PageUp', 'PageDown'];
        static FALLBACK_DELAY = 100;
        static SLIDE_ACTIVE_THRESHOLD = 0.5;
        static SLIDE_EXIT_THRESHOLD = 0.9;
        static FALLBACK_THRESHOLD = 0.3;
        static CONTENT_TRANSITION_DURATION = 600;
        static SCROLL_DEBOUNCE = 50;

        constructor() {
            super();
            this.video = null;
            this.slides = [];
            this.mainContent = null;
            this.section = null;
            this.currentSlideIndex = -1;
            this.observers = [];
            this.lastScrollTop = 0;
            this.scrollDirection = 'down';
            this.backwardInterval = null;
        }

        connectedCallback() {
            this.video = this.querySelector('video');
            this.slides = Array.from(this.querySelectorAll('[data-slide]'));
            this.mainContent = document.querySelector('.main-content');
            this.section = this.closest('.shopify-section');

            if (!this.mainContent || !this.section || !this.slides.length) return;

            this.setupScrollHandlers();
            this.setupSentinel();
            this.setupSlideObservers();
            this.setupVideoTimeupdate();

            this.sectionObserver = new IntersectionObserver((entries) => {
                const e = entries[0];
                const r = e.intersectionRatio;
                if (e.isIntersecting && r > 0.12) {
                    this.classList.add('is-active');
                    if (this.video) {
                        this.video.style.visibility = 'visible';
                        this.video.style.opacity = '1';
                    }
                    return;
                }
                if (this.scrollDirection === 'up' && r < 0.04) {
                    this.classList.remove('is-active');
                    if (this.video) {
                        this.video.pause();
                        this.video.style.visibility = 'hidden';
                        this.video.style.opacity = '0';
                    }
                    return;
                }
                if (!e.isIntersecting) {
                    this.classList.remove('is-active');
                    if (this.video) {
                        this.video.pause();
                        this.video.style.visibility = 'hidden';
                        this.video.style.opacity = '0';
                    }
                }
            }, { 
                root: this.mainContent, 
                threshold: [0, 0.04, 0.12, 0.5, 0.9, 1] 
            });

            this.sectionObserver.observe(this.section);
        }

        initializeVideoLazy() {
            if (!this.video || this.videoLoaded) return;

            this.video.load();
            this.videoLoaded = true;

            this.setupVideoTimeupdate();
        }

        setupScrollHandlers() {
            this.mainContent.addEventListener('scroll', () => {
                const currentScrollTop = this.mainContent.scrollTop;
                this.scrollDirection = currentScrollTop > this.lastScrollTop ? 'down' : 'up';
                this.lastScrollTop = currentScrollTop;
            });
        }

        setupSentinel() {
            this.topSentinel = this.querySelector('[data-snap]') || this.createSentinel();
        }

        createSentinel() {
            const sentinel = document.createElement('div');
            sentinel.setAttribute('data-snap', '');
            sentinel.className = 'landing-features__sentinel';
            sentinel.setAttribute('aria-hidden', 'true');
            this.prepend(sentinel);
            return sentinel;
        }

        setupSlideObservers() {
            let videoInitialized = false;
            const isFirstSlide = (slideIndex) => slideIndex === 0;

            // Observer for each slide - controls video visibility, playback, and content
            this.slides.forEach((slide, index) => {
                const observer = new IntersectionObserver((entries) => {
                    const { isIntersecting, intersectionRatio, boundingClientRect } = entries[0];
                    const content = slide.querySelector('.landing-features__slide__content');

                    if (!videoInitialized && isIntersecting && intersectionRatio > 0) {
                        videoInitialized = true;
                        this.initializeVideoLazy();
                    }

                    if (isFirstSlide(index)) {
                        if (isIntersecting && intersectionRatio > 0.3) {
                            // Keep video active a bit longer when scrolling up
                            this.classList.add('is-active');
                        } else if (this.scrollDirection === 'up' && boundingClientRect.bottom <= 0) {
                            // Only remove when section has fully left viewport upwards
                            this.classList.remove('is-active');
                        }
                    } else {
                        if (isIntersecting && intersectionRatio > 0) {
                            this.classList.add('is-active');
                        }
                    }

                    if (isIntersecting && intersectionRatio > 0.5) {
                        if (this.currentSlideIndex !== index) {
                            this.hideAllSlideContent();
                            this.currentSlideIndex = index;
                            this.playSlideVideo(slide, this.scrollDirection);
                        }
                    }
                    else if (intersectionRatio < 0.1) {
                        if (this.currentSlideIndex === index) {
                            this.currentSlideIndex = -1;
                        }
                        content?.classList.remove('is-visible');

                        // Hide video when last slide exits downward
                        if (index === this.slides.length - 1 && !isIntersecting && boundingClientRect.top < 0) {
                            this.classList.remove('is-active');
                        }
                    }
                }, { root: this.mainContent, threshold: [0, 0.1, 0.3, 0.5, 0.9] });

                observer.observe(slide);
                this.observers.push(observer);
            });
        }

        setupVideoTimeupdate() {
            if (!this.video) return;

            this.video.addEventListener('timeupdate', () => {
                if (this.backwardInterval || this.currentSlideIndex < 0) return;

                const currentSlide = this.slides[this.currentSlideIndex];
                const startTime = this.getSlideTimestamp(currentSlide, 'start-timestamp');
                const endTime = this.getSlideTimestamp(currentSlide, 'end-timestamp');

                if ((startTime || endTime) && endTime > 0) {
                    const transitionStartTime = (endTime - this.constructor.CONTENT_TRANSITION_DURATION) / 1000;

                    if (this.video.currentTime >= transitionStartTime && !currentSlide.querySelector('.landing-features__slide__content')?.classList.contains('is-visible')) {
                        this.showSlideContent(currentSlide);
                    }

                    if (this.video.currentTime >= endTime / 1000) {
                        this.video.pause();
                    }
                }
            });
        }

        showSlideContent(slide) {
            slide.querySelector('.landing-features__slide__content')?.classList.add('is-visible');
        }

        hideAllSlideContent() {
            this.slides.forEach(s => s.querySelector('.landing-features__slide__content')?.classList.remove('is-visible'));
        }

        getSlideTimestamp(slide, attribute) {
            return parseInt(slide.getAttribute(`data-${attribute}`), 10) || 0;
        }

        playSlideVideo(slide, direction = 'down') {
            if (!this.video) return;

            const startTime = this.getSlideTimestamp(slide, 'start-timestamp');
            const endTime = this.getSlideTimestamp(slide, 'end-timestamp');

            // Cancel any ongoing backward animation
            if (this.backwardInterval) {
                cancelAnimationFrame(this.backwardInterval);
                this.backwardInterval = null;
            }

            // Slide has no video timestamps - just show content
            if (!startTime && !endTime) {
                this.video.pause();
                if (direction === 'down') {
                    this.showSlideContent(slide);
                    return;
                }
                // Scrolling up to a slide with no video - rewind to start
                this.playVideoBackwards(0, true, () => {
                    this.showSlideContent(slide);
                });
                return;
            }

            // Scrolling up - play video backwards to this slide's position
            if (direction === 'up') {
                const targetTime = (endTime || startTime) / 1000;
                this.playVideoBackwards(targetTime, true, () => {
                    this.showSlideContent(slide);
                });
                return;
            }

            // Scrolling down - play video forward
            const playVideo = () => {
                this.video.currentTime = startTime / 1000;
                this.video.play().catch(() => { });
            };

            if (this.video.readyState >= 2) {
                playVideo();
            } else {
                this.video.addEventListener('canplay', playVideo, { once: true });
            }

            // If slide has only start time (no end), show content immediately
            if (!endTime && startTime) {
                this.showSlideContent(slide);
            }
        }

        /**
         * @param {number} targetTime
         * @param {boolean} stopAtTarget
         * @param {(() => void) | null} [onComplete]
         */
        playVideoBackwards(targetTime, stopAtTarget = false, onComplete = null) {
            if (!this.video) return;

            if (this.backwardInterval) {
                cancelAnimationFrame(this.backwardInterval);
            }

            if (this.video.readyState < 2) {
                this.video.addEventListener('canplay', () => {
                    this.playVideoBackwards(targetTime, stopAtTarget, onComplete);
                }, { once: true });
                return;
            }

            const FPS = 12;
            const frameDuration = 1000 / FPS;
            let lastTime = performance.now();

            const animate = (now) => {
                const elapsed = now - lastTime;
                if (elapsed >= frameDuration) {
                    lastTime = now;
                    const newTime = this.video.currentTime - (elapsed / 1000);

                    if (newTime <= targetTime + 0.05) {
                        cancelAnimationFrame(this.backwardInterval);
                        this.video.pause();

                        //Clamp slightly before target to prevent black frame
                        const safeTarget = Math.max(0, targetTime - 0.03);
                        this.video.currentTime = safeTarget;

                        if (onComplete) onComplete();
                        return;
                    }

                    this.video.currentTime = newTime;
                }

                this.backwardInterval = requestAnimationFrame(animate);
            };

            this.backwardInterval = requestAnimationFrame(animate);
        }

        disconnectedCallback() {
            // Cleanup observers
            this.observers.forEach(o => o?.disconnect());

            // Cancel any ongoing backward animation
            if (this.backwardInterval) {
                cancelAnimationFrame(this.backwardInterval);
            }
        }
    });
}